import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Loader2, Lock, Mail, User as UserIcon, CheckCircle2, AlertTriangle, Phone, Sparkles } from 'lucide-react';
import { apiClient } from '../api/client';
import { useAuth } from '../contexts/AuthContext';

/**
 * Parent Onboarding (Magic Link)
 * ──────────────────────────────
 * Lands here when a parent clicks the email/SMS link after their teen signs up.
 *
 *  URL:  /parent-onboarding?token=…
 *  Flow:
 *    1. Validate token → backend returns pre-fill payload (parent name/email/
 *       phone + teen name/age).
 *    2. Parent sets a password + accepts terms.
 *    3. POST /api/auth/parent-onboarding/complete → backend creates the
 *       parent account, links the teen, signs the charter, issues a JWT.
 *    4. Parent lands on /pricing already logged-in to pick a family plan.
 */

const PURPLE = '#7B4FD8';
const ORANGE = '#F5831F';

const ParentOnboarding = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const token = searchParams.get('token');

  const [phase, setPhase] = useState('validating'); // validating | invalid | ready | submitting | done
  const [error, setError] = useState(null);
  const [payload, setPayload] = useState(null);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [acceptTerms, setAcceptTerms] = useState(false);

  useEffect(() => {
    if (!token) {
      setPhase('invalid');
      setError('Missing invitation token. Ask your teen to resend the link from their dashboard.');
      return;
    }
    let cancelled = false;
    apiClient.request(`/api/auth/parent-onboarding/validate?token=${encodeURIComponent(token)}`)
      .then((res) => {
        if (cancelled) return;
        setPayload(res);
        setPhase('ready');
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.message || 'Invitation link is no longer valid.');
        setPhase('invalid');
      });
    return () => { cancelled = true; };
  }, [token]);

  const submit = async (e) => {
    e?.preventDefault();
    setError(null);
    const isExisting = !!payload?.existing_account;
    if (!isExisting) {
      if (password.length < 8) {
        setError('Password must be at least 8 characters.');
        return;
      }
      if (password !== confirm) {
        setError('Passwords don\'t match.');
        return;
      }
    }
    if (!acceptTerms) {
      setError('Please accept the Terms of Service and Privacy Policy to continue.');
      return;
    }
    setPhase('submitting');
    try {
      const res = await apiClient.request('/api/auth/parent-onboarding/complete', {
        method: 'POST',
        body: JSON.stringify({ token, password: isExisting ? '' : password, accept_terms: acceptTerms }),
      });
      // Persist the freshly-issued JWT, then hydrate AuthContext so the
      // parent lands on /pricing already logged-in.
      if (res.access_token) {
        apiClient.setToken(res.access_token, true);
        try { await refreshUser(); } catch {}
      }
      setPhase('done');
      // Auto-redirect after a beat so the parent reads the confirmation.
      setTimeout(() => navigate(res.next_step || '/pricing'), 1500);
    } catch (err) {
      setError(err?.message || 'Could not complete onboarding. Please try again.');
      setPhase('ready');
    }
  };

  // ── States ────────────────────────────────────────────────────────────
  if (phase === 'validating') {
    return (
      <Wrap>
        <div className="text-center py-16">
          <Loader2 size={42} className="animate-spin mx-auto mb-4" style={{ color: PURPLE }} />
          <p className="text-gray-600">Checking your invitation…</p>
        </div>
      </Wrap>
    );
  }

  if (phase === 'invalid') {
    return (
      <Wrap>
        <div className="text-center py-12">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4" style={{ background: '#FEF3F2' }}>
            <AlertTriangle size={28} style={{ color: '#B42318' }} />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">This link can&apos;t be used</h1>
          <p className="text-gray-600 mb-8 max-w-sm mx-auto">{error}</p>
          <button onClick={() => navigate('/login')} className="px-6 py-3 rounded-full font-semibold text-white" style={{ background: PURPLE }} data-testid="parent-onboarding-signin-instead">Sign in instead</button>
          <p className="text-xs text-gray-500 mt-3">Already activated your account? Just sign in with your email and password.</p>
        </div>
      </Wrap>
    );
  }

  if (phase === 'done') {
    return (
      <Wrap>
        <div className="text-center py-16">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4" style={{ background: '#ECFDF5' }}>
            <CheckCircle2 size={32} style={{ color: '#059669' }} />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Approved. Their chat is unlocked.</h1>
          <p className="text-gray-600 mb-4">Welcome to HiBFF — redirecting you to pick a family plan…</p>
          <Loader2 size={20} className="animate-spin mx-auto" style={{ color: PURPLE }} />
        </div>
      </Wrap>
    );
  }

  // ── Ready / submitting — the actual signup form ───────────────────────
  const isExisting = !!payload?.existing_account;
  return (
    <Wrap>
      <div data-testid="parent-onboarding-form-root">
        {/* Hero */}
        <div
          className="rounded-3xl p-6 sm:p-8 mb-8 text-white relative overflow-hidden"
          style={{ background: `linear-gradient(135deg, ${PURPLE} 0%, #5B2BB8 100%)` }}
        >
          <div className="absolute inset-0" style={{ background: 'radial-gradient(circle at 80% 20%, rgba(245, 131, 31, 0.25), transparent 50%)', pointerEvents: 'none' }} />
          <div className="relative">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/15 mb-3 text-[11px] uppercase tracking-wider font-bold">
              <Sparkles size={12} /> Family invitation
            </div>
            <h1 className="text-2xl sm:text-3xl font-extrabold mb-1.5">
              {(payload?.teen_name || 'Your teen').split(' ')[0]} just signed up to HiBFF.
            </h1>
            <p className="text-white/85 text-sm sm:text-base max-w-md">
              {isExisting
                ? 'You already have a HiBFF account — approve their account in one tap and their coaching unlocks.'
                : 'Their AvA chat stays locked until you approve. Finish your parent account in 90 seconds — pick a password and you\u2019re done.'}
            </p>
          </div>
        </div>

        {/* Pre-filled details (read-only context) */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
          <ReadField label="Your name" value={payload?.parent_name} icon={<UserIcon size={14} />} />
          <ReadField label="Your email" value={payload?.parent_email} icon={<Mail size={14} />} />
          <ReadField label="Your mobile" value={payload?.parent_phone || '—'} icon={<Phone size={14} />} />
          <ReadField label="Your teen" value={payload?.teen_name + (payload?.teen_age ? `, age ${payload.teen_age}` : '')} icon={<UserIcon size={14} />} />
        </div>

        <form onSubmit={submit} className="space-y-4" data-testid="parent-onboarding-form">
          {!isExisting && (
            <>
              <PasswordField
                label="Choose a password"
                value={password}
                onChange={setPassword}
                placeholder="At least 8 characters"
                testid="parent-onboarding-password"
              />
              <PasswordField
                label="Confirm password"
                value={confirm}
                onChange={setConfirm}
                placeholder="Type it again"
                testid="parent-onboarding-confirm"
              />
            </>
          )}

          <label className="flex items-start gap-3 p-3 rounded-xl text-sm bg-gray-50 border border-gray-200 cursor-pointer">
            <input
              type="checkbox"
              checked={acceptTerms}
              onChange={(e) => setAcceptTerms(e.target.checked)}
              className="mt-0.5"
              data-testid="parent-onboarding-terms"
            />
            <span className="text-gray-700">
              I accept the <a href="/terms" target="_blank" className="underline" style={{ color: PURPLE }}>Terms of Service</a> and <a href="/privacy" target="_blank" className="underline" style={{ color: PURPLE }}>Privacy Policy</a>, and I consent to {(payload?.teen_name || 'my teen').split(' ')[0]} having a HiBFF account under my supervision.
            </span>
          </label>

          {error && (
            <div className="p-3 rounded-lg text-sm" style={{ background: '#FEF3F2', color: '#B42318', border: '1px solid #FDA29B' }} data-testid="parent-onboarding-error">{error}</div>
          )}

          <button
            type="submit"
            disabled={phase === 'submitting'}
            className="w-full px-6 py-4 rounded-full font-bold text-white text-base disabled:opacity-60 transition-transform hover:scale-[1.01] active:scale-[0.99]"
            style={{ background: `linear-gradient(135deg, ${ORANGE}, #FF9F4A)`, boxShadow: `0 4px 20px ${ORANGE}55` }}
            data-testid="parent-onboarding-submit"
          >
            {phase === 'submitting'
              ? <><Loader2 size={16} className="inline animate-spin mr-2" /> {isExisting ? 'Approving…' : 'Activating…'}</>
              : <>{isExisting ? 'Approve & unlock their account' : 'Approve, activate account & continue'}</>}
          </button>

          <p className="text-[11px] text-gray-500 text-center">
            Subscription charges appear as <span className="font-mono">HIBFF TEEN COACHING</span>. Cancel anytime · 30-day refund · 90-day pause.
          </p>
        </form>
      </div>
    </Wrap>
  );
};

const Wrap = ({ children }) => (
  <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-orange-50 py-12 px-4">
    <div className="max-w-xl mx-auto bg-white rounded-3xl shadow-xl p-6 sm:p-10" data-testid="parent-onboarding-page">
      <img src="/hibff-logo.png" alt="HiBFF" className="h-12 mx-auto mb-6" />
      {children}
    </div>
  </div>
);

const ReadField = ({ label, value, icon }) => (
  <div className="rounded-xl px-3 py-2.5 bg-gray-50 border border-gray-200">
    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-semibold text-gray-500 mb-0.5">
      {icon}{label}
    </div>
    <div className="text-sm font-semibold text-gray-900 truncate">{value || '—'}</div>
  </div>
);

const PasswordField = ({ label, value, onChange, placeholder, testid }) => (
  <div>
    <label className="block text-sm font-semibold text-gray-700 mb-1.5">{label}</label>
    <div className="relative">
      <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
      <input
        type="password"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
        data-testid={testid}
        required
      />
    </div>
  </div>
);

export default ParentOnboarding;
